import React from 'react';
import { useTranslation } from 'react-i18next';
import { AuthWrapper, AuthFormHeader, BackupCodesDisplay } from '@src/features/auth/components';
import { useMfaEnrollment } from '@src/features/auth/hooks';
import { REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { ArcBox, ArcButton, ArcDivider, ArcOutlinedInput, ArcSkeleton, ArcStack, ArcText } from '@src/core/ui';

const MfaEnrollmentPage: React.FC = () => {
  const { t } = useTranslation();

  // Numbered, user-facing onboarding guide shown directly on the enrollment screen
  // (explicitly required to be visible, not a tooltip).
  const enrollmentSteps = [
    t('mfa.enroll.instructions.step1'),
    t('mfa.enroll.instructions.step2'),
    t('mfa.enroll.instructions.step3'),
    t('mfa.enroll.instructions.step4'),
  ];

  const {
    register,
    formState: { errors },
    onSubmit,
    setupData,
    isSettingUp,
    isVerifying,
    errorMessage,
    backupCodes,
    onFinish,
  } = useMfaEnrollment();

  // Step 2: enrollment succeeded — show the backup codes exactly once.
  if (backupCodes) {
    return (
      <AuthWrapper>
        <AuthFormHeader
          title={t('mfa.enroll.backupTitle')}
          subtitle={t('mfa.enroll.backupSubtitle')}
        />
        <ArcStack mt={4} spacing={3}>
          <BackupCodesDisplay codes={backupCodes} />
          <ArcButton.brand onClick={onFinish} sx={{ alignSelf: 'center', width: 300 }}>
            {t('mfa.enroll.savedCodes')}
          </ArcButton.brand>
        </ArcStack>
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper>
      <AuthFormHeader
        title={t('mfa.enroll.title')}
        subtitle={t('mfa.enroll.subtitle')}
      />

      <ArcStack mt={4} spacing={3}>
        {/* Step-by-step instructions.
            The panel sits on the `background.paper` card, so it uses the translucent
            `action.hover` overlay (a touch lighter than the card) for a visible
            surface, and explicit text colors so the steps read clearly — the base
            ArcText has no default color of its own. */}
        <ArcBox
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <ArcText fontSize={15} fontWeight={700} mb={1.5} color='text.primary'>
            {t('mfa.enroll.howTo')}
          </ArcText>
          <ArcStack spacing={1.25} sx={{ m: 0 }}>
            {/* The step number is part of each translated string, so we render a
                plain list (not an <ol>) to avoid double numbering. */}
            {enrollmentSteps.map((step) => (
              <ArcText key={step} fontSize={14} lineHeight={1.5} color='text.primary'>
                {step}
              </ArcText>
            ))}
          </ArcStack>
        </ArcBox>

        <ArcDivider />

        {/* QR code + manual secret */}
        {isSettingUp && <ArcSkeleton height={220} />}

        {setupData && (
          <ArcStack spacing={2} alignItems='center'>
            <img
              src={setupData.qrPngBase64}
              alt={t('mfa.enroll.qrAlt')}
              style={{ width: 200, height: 200, borderRadius: 4, backgroundColor: 'white', padding: 8 }}
            />
            <ArcStack spacing={0.5} alignItems='center'>
              <ArcText fontSize={13} color='text.secondary'>
                {t('mfa.enroll.cannotScan')}
              </ArcText>
              <ArcText
                fontSize={15}
                fontFamily='monospace'
                color='text.primary'
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  letterSpacing: 1,
                  wordBreak: 'break-all',
                  textAlign: 'center',
                }}
              >
                {setupData.secret}
              </ArcText>
            </ArcStack>
          </ArcStack>
        )}

        {/* Code confirmation */}
        <ArcStack component='form' spacing={3} alignItems='center' onSubmit={onSubmit}>
          <ArcOutlinedInput
            fullWidth
            autoFocus
            autoComplete='one-time-code'
            label={t('mfa.enroll.codeLabel')}
            error={Boolean(errors.code) || Boolean(errorMessage)}
            helperText={errors.code?.message || errorMessage || ''}
            {...register('code', { required: REQUIRED_VALIDATION_TEXT })}
          />

          <ArcButton.brand
            type='submit'
            sx={{ width: 300 }}
            isLoading={isVerifying}
            disabled={!setupData}
          >
            {t('mfa.enroll.submit')}
          </ArcButton.brand>
        </ArcStack>
      </ArcStack>
    </AuthWrapper>
  );
};

export default MfaEnrollmentPage;
